import {
  injectable,
  inject,
  postConstruct,
  IGwApiService as ICoreIGwApiService,
} from '@meego-remote/core/app/contract'
import type { GwApiService } from '@meego-remote/core/app/service'
import { apiMap, type ApiServiceReqRes } from '@meego-setting/idl-api/apiMap'
import { ISettingApiService, type ExtractResponseData } from '#contract'

export type SettingApiServiceType = ApiServiceReqRes

/**
 * 扩展与隔离主仓 ApiService，setting 领域内使用数据源内部 ApiService
 *
 * - 提供 apiMap 注册与使用时类型扩展
 * - 提供 RPC name 以及便于跳转
 *
 * @example
 * ```ts
 * import { inject } from '@meego-remote/core/app/contract'
 * import { ISettingApiService, RPC } from '@meego-setting/datasource/contract'
 *
 * @inject(ISettingApiService)
 * private settingApiService!: ISettingApiService;
 *
 * this.settingApiService.request(RPC.APIGetAIConfigSchema, { ... })
 * ```
 *
 * > 仅类型约束使用 `ICoreIGwApiService`，实际不注册到 `ICoreIGwApiService` 中
 */
@injectable({ implements: [ISettingApiService] })
export class SettingApiService implements ISettingApiService {
  @inject(ICoreIGwApiService)
  private gwApiService!: GwApiService & ICoreIGwApiService

  @postConstruct()
  public init() {
    // @FIXME: 当前 page server 提供的 api 服务没有注册能力, 需要基座补全
    this.gwApiService.registerApi?.(apiMap)
  }

  public request<RPC extends keyof ApiServiceReqRes>(
    apiName: RPC,
    data: ApiServiceReqRes[RPC]['req'],
    requestConfig?: Record<string, any>,
  ): Promise<ExtractResponseData<Awaited<ApiServiceReqRes[RPC]['res']>>> {
    return this.gwApiService.request(apiName, data, requestConfig)
      .then(res => {
        /**
         * - Lark 网关会统一把 RPC 接口输出格式嵌套进一层 { data: ... }
         * - 主仓 axios 会统一把接口返回格式嵌套进一层 { data: ... }
         *
         * 由主仓中拦截器处理统一报错与错误码
         */
        return res.data.data
      })
  }
}

